import { api, unwrap } from '@/api'

export interface CarBrand {
  id: number
  name: string
  slug: string
  logoUrl?: string
  isFeatured: boolean
  isActive: boolean
  displayOrder: number
  models?: CarModel[]
  _count?: { models: number; listings: number }
}

export interface CarModel {
  id: number
  brandId: number
  name: string
  slug: string
  bodyType?: string
  isActive: boolean
  displayOrder: number
  _count?: { listings: number }
}

export interface CarGeneration {
  id: number
  modelId: number
  name: string
  startYear: number
  endYear?: number
  isActive: boolean
}

export interface CarVariant {
  id: number
  generationId: number
  name: string
  fuelType?: string
  transmission?: string
  engineCc?: number
  horsepower?: number
  torqueNm?: number
  mileageKmpl?: number
  seatingCapacity?: number
  isActive: boolean
  specifications?: any
}

export interface CarFeature {
  id: number
  category: string
  name: string
  code: string
  description?: string
  isActive: boolean
}

export interface CarListing {
  id: number
  userId: number
  brandId: number
  modelId: number
  generationId?: number
  variantId?: number
  title: string
  slug: string
  askingPrice: number
  negotiable: boolean
  manufactureYear: number
  registrationYear?: number
  mileageKm: number
  fuelType: string
  transmission: string
  driveType?: string
  condition: string
  ownership: string
  exteriorColor?: string
  interiorColor?: string
  locationCity: string
  locationState: string
  description?: string
  features?: string[]
  images: { url: string; isPrimary?: boolean; order?: number }[]
  videoUrl?: string
  status: 'DRAFT' | 'PENDING_REVIEW' | 'PUBLISHED' | 'REJECTED' | 'SOLD' | 'EXPIRED' | 'ARCHIVED'
  rejectionReason?: string
  isFeatured: boolean
  isBoosted: boolean
  viewsCount: number
  publishedAt?: string
  createdAt: string
  updatedAt: string
  brand?: CarBrand
  model?: CarModel
  generation?: CarGeneration
  variant?: CarVariant
  user?: { id: number; firstName: string; lastName: string; email?: string; phone?: string; role: string; avatar?: string }
}

export const carsService = {
  // Public Master Data Read
  async getBrands(): Promise<CarBrand[]> {
    const res = await api.get('/cars/brands')
    return unwrap(res) as CarBrand[]
  },

  async getModels(brandId: number): Promise<CarModel[]> {
    const res = await api.get(`/cars/brands/${brandId}/models`)
    return unwrap(res) as CarModel[]
  },

  async getGenerations(modelId: number): Promise<CarGeneration[]> {
    const res = await api.get(`/cars/models/${modelId}/generations`)
    return unwrap(res) as CarGeneration[]
  },

  async getVariants(generationId: number): Promise<CarVariant[]> {
    const res = await api.get(`/cars/generations/${generationId}/variants`)
    return unwrap(res) as CarVariant[]
  },

  async getFeatures(): Promise<CarFeature[]> {
    const res = await api.get('/cars/features')
    return unwrap(res) as CarFeature[]
  },

  // Public Marketplace Read
  async getMarketplaceListings(params: any): Promise<{ items: CarListing[]; total: number; page: number; limit: number; totalPages: number }> {
    const res = await api.get('/cars/marketplace', { params })
    return unwrap(res) as any
  },

  async getListingBySlug(slug: string): Promise<{ listing: CarListing; related: CarListing[] }> {
    const res = await api.get(`/cars/details/${slug}`)
    return unwrap(res) as any
  },

  // Seller / Dealer Inventory
  async getMyListings(status?: string): Promise<CarListing[]> {
    const res = await api.get('/cars/my-listings', { params: { status } })
    return unwrap(res) as CarListing[]
  },

  async createListing(payload: any): Promise<CarListing> {
    const res = await api.post('/cars/listings', payload)
    return unwrap(res) as CarListing
  },

  async updateListing(id: number, payload: any): Promise<CarListing> {
    const res = await api.put(`/cars/listings/${id}`, payload)
    return unwrap(res) as CarListing
  },

  async archiveListing(id: number): Promise<CarListing> {
    const res = await api.delete(`/cars/listings/${id}`)
    return unwrap(res) as CarListing
  },

  async requestModel(payload: any): Promise<any> {
    const res = await api.post('/cars/request-model', payload)
    return unwrap(res)
  },

  // Admin Master Data & Moderation
  async adminCreateBrand(payload: any): Promise<CarBrand> {
    const res = await api.post('/platform/admin/cars/brands', payload)
    return unwrap(res) as CarBrand
  },

  async adminCreateModel(payload: any): Promise<CarModel> {
    const res = await api.post('/platform/admin/cars/models', payload)
    return unwrap(res) as CarModel
  },

  async adminCreateGeneration(payload: any): Promise<CarGeneration> {
    const res = await api.post('/platform/admin/cars/generations', payload)
    return unwrap(res) as CarGeneration
  },

  async adminCreateVariant(payload: any): Promise<CarVariant> {
    const res = await api.post('/platform/admin/cars/variants', payload)
    return unwrap(res) as CarVariant
  },

  async adminCreateFeature(payload: any): Promise<CarFeature> {
    const res = await api.post('/platform/admin/cars/features', payload)
    return unwrap(res) as CarFeature
  },

  async adminGetListings(status?: string): Promise<CarListing[]> {
    const res = await api.get('/platform/admin/cars/listings', { params: { status } })
    return unwrap(res) as CarListing[]
  },

  async adminApproveListing(id: number): Promise<CarListing> {
    const res = await api.post(`/platform/admin/cars/listings/${id}/approve`)
    return unwrap(res) as CarListing
  },

  async adminRejectListing(id: number, reason: string): Promise<CarListing> {
    const res = await api.post(`/platform/admin/cars/listings/${id}/reject`, { reason })
    return unwrap(res) as CarListing
  },

  async adminGetModelRequests(): Promise<any[]> {
    const res = await api.get('/platform/admin/cars/model-requests')
    return unwrap(res) as any[]
  },

  // Platform Entitlement & Usage
  async getPlatformUsage(): Promise<{
    remainingCars: number
    remainingParts: number
    remainingBoostCredits: number
    remainingLeadCredits: number
    remainingDraftCars: number
    maxCars: number
    maxDraftCars: number
    maxImagesPerListing: number
  }> {
    const res = await api.get('/platform/usage')
    return unwrap(res) as any
  },

  async getPlatformEntitlement(): Promise<any> {
    const res = await api.get('/platform/entitlement')
    return unwrap(res) as any
  },

  async getPlatformSubscription(): Promise<any> {
    const res = await api.get('/platform/subscriptions/me')
    return unwrap(res) as any
  },
}
